import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import CheckoutSheet from "@/components/checkout/CheckoutSheet";
import { SheetBody, SheetFooter } from "../ui/SheetHost";
import CashierTabs from "../ui/CashierTabs";

/* CoinStoreSheet (design 5c) — Get Coins. Packs are a radio list from the
   backend (GET /payments/packages): PCoins big, free PCash beneath, price on the
   right. One sticky CTA names the pack + price and leads into checkout (5d/5e),
   which reuses the existing branded CheckoutSheet (Stripe PaymentIntent, success
   with real credited amounts + refreshed balances).
   HONEST: first-purchase bonus reads "amount TBD" until the backend reports a
   real bonus %; NO hardcoded packs. Export shape: ({ close }). */

const GREEN = "#00D632";
const SILVER = "#E4E4E7";
const fmtInt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function CoinStoreSheet({ close, openCashier }) {
  const [packages, setPackages] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [firstBonus, setFirstBonus] = useState({ available: false, coinPct: 0, cashPct: 0 });
  const [selected, setSelected] = useState(null); // lookup_key
  const [checkout, setCheckout] = useState(null);  // { order } → reused CheckoutSheet

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => {
      const packs = Array.isArray(data?.packages) ? data.packages : [];
      setPackages(packs);
      setFirstBonus({ available: !!data?.first_purchase_available, coinPct: data?.first_purchase_coin_pct || 0, cashPct: data?.first_purchase_cash_pct || 0 });
      // Pre-select a mid pack if present, else the first.
      if (packs.length) setSelected(packs[Math.min(2, packs.length - 1)].lookup_key);
    }).catch(() => setLoadErr(true));
  }, []);

  const sel = useMemo(() => (packages || []).find((p) => p.lookup_key === selected) || null, [packages, selected]);
  // Whether this pack's first-purchase bonus % resolves to a REAL number (else "amount TBD").
  const bonusKnown = firstBonus.available && (firstBonus.coinPct > 0 || firstBonus.cashPct > 0);

  const openCheckout = () => {
    if (!sel) return;
    const bc = bonusKnown ? Math.round(sel.coins * firstBonus.coinPct / 100) : 0;
    const bch = bonusKnown ? +(sel.cash * firstBonus.cashPct / 100).toFixed(2) : 0;
    setCheckout({
      order: {
        lookup_key: sel.lookup_key, custom_usd: null,
        label: sel.label, usd: sel.usd, coins: sel.coins, cash: sel.cash,
        firstBonusAvailable: bonusKnown,
        firstBonusCoins: bc, firstBonusCash: bch,
      },
    });
  };

  return (
    <>
      <CashierTabs active="get" close={close} openCashier={openCashier} />
      <SheetBody>
        {/* first-purchase bonus banner — honest TBD when the % isn't set */}
        {firstBonus.available && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 52, padding: "0 14px", borderRadius: 14, background: "rgba(0,214,50,.07)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.3)", marginBottom: 14 }}>
            <Icon name="celebrate" size={18} style={{ color: GREEN, flex: "none" }} />
            <span style={{ font: "700 13px 'Outfit'", color: "#fff" }}>
              First purchase bonus {bonusKnown
                ? <span style={{ color: GREEN }}>· +{firstBonus.coinPct}% PCoins{firstBonus.cashPct ? ` + ${firstBonus.cashPct}% PCash` : ""}</span>
                : <span style={{ color: "#a1a1aa" }}>· amount TBD</span>}
            </span>
          </div>
        )}

        {loadErr ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 16px" }}>
            <span style={{ font: "500 13px 'Outfit'", color: "#a1a1aa" }}>We couldn't load coin packs.</span>
            <button type="button" onClick={() => { setLoadErr(false); setPackages(null); api.get("/payments/packages").then(({ data }) => { setPackages(data?.packages || []); if (data?.packages?.length) setSelected(data.packages[0].lookup_key); }).catch(() => setLoadErr(true)); }}
              style={{ height: 40, padding: "0 18px", borderRadius: 12, background: "#18181C", color: "#fff", font: "700 14px 'Outfit'", border: 0, cursor: "pointer", boxShadow: "inset 0 0 0 1px #2F2F35" }}>Try again</button>
          </div>
        ) : !packages ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[0, 1, 2, 3].map((i) => <span key={i} aria-hidden="true" style={{ height: 70, borderRadius: 14, background: "#141417" }} />)}
          </div>
        ) : packages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "28px 16px", font: "500 13px 'Outfit'", color: "#a1a1aa" }}>No coin packs available right now.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }} role="radiogroup" aria-label="Coin packs">
            {packages.map((p) => {
              const on = selected === p.lookup_key;
              return (
                <button key={p.lookup_key} type="button" role="radio" aria-checked={on} onClick={() => setSelected(p.lookup_key)}
                  style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 70, padding: "10px 14px", borderRadius: 14, border: 0, cursor: "pointer", width: "100%", textAlign: "left",
                    background: on ? "rgba(0,214,50,.07)" : "#141417",
                    boxShadow: on ? "inset 0 0 0 1.5px #00D632" : "inset 0 0 0 1px #26262B" }}>
                  <Icon name="pnrcoin" size={30} style={{ color: GREEN, flex: "none" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="m-tnum" style={{ font: "800 18px 'Manrope'", color: "#fff" }}>{fmtInt(p.coins)} <span style={{ font: "600 12px 'Outfit'", color: "#a1a1aa" }}>PCoins</span></div>
                    <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>{p.label}</div>
                    {p.cash > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2, font: "500 12px 'Outfit'", color: "#a1a1aa" }}>
                        <Icon name="pnrcash" size={12} style={{ color: SILVER }} /> + {fmtUsd(p.cash)} PCash free
                      </div>
                    )}
                  </div>
                  <span className="m-tnum" style={{ flex: "none", height: 30, display: "inline-flex", alignItems: "center", padding: "0 11px", borderRadius: 11, font: "800 15px 'Manrope'",
                    background: on ? GREEN : "#1C1C21", color: on ? "#0A0A0A" : "#fff" }}>
                    {fmtUsd(p.usd)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </SheetBody>

      <SheetFooter>
        <button type="button" onClick={openCheckout} disabled={!sel}
          style={{ width: "100%", height: 56, borderRadius: 16, background: GREEN, color: "#0A0A0A", font: "800 16px 'Manrope'", border: 0, cursor: sel ? "pointer" : "default", opacity: sel ? 1 : 0.4, boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {sel ? `Buy ${sel.label} · ${fmtUsd(sel.usd)}` : "Choose a pack"}
        </button>
        <p style={{ font: "500 11px/1.5 'Outfit'", color: "#71717a", marginTop: 10, textAlign: "center" }}>
          No purchase necessary · Free entry by mail · 18+
        </p>
      </SheetFooter>

      {/* Checkout (5d) + Purchase success (5e) — reuse the branded CheckoutSheet
          (Stripe PaymentIntent → success with real credited amounts + refreshed
          balances). It renders through a portal above this sheet. */}
      <CheckoutSheet
        open={!!checkout}
        order={checkout?.order}
        onClose={() => { setCheckout(null); close?.(); }}
      />
    </>
  );
}
